// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { BunkerIcon, PenaltyIcon, ThreePuttIcon, WaterIcon } from './assets';
import type { HoleEntry, HoleEventEntry, NetResult, ScoreTotal } from './types';

export const normalizePlayerKey = (key: string | undefined | null) => key?.trim().toLowerCase() ?? '';
export const isUuidLike = (value?: string) =>
  !!value && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

export const compactPlayerName = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return parts[0] ?? name;
  const initial = parts[1]?.[0] ?? '';
  return initial ? `${parts[0]} ${initial}.` : parts[0];
};

export const hazardTypeFromName = (name?: string) => {
  const normalized = (name ?? '').toLowerCase();
  if (normalized.includes('water')) return 'water';
  if (normalized.includes('penalty')) return 'penalty';
  if (normalized.includes('bunker')) return 'bunker';
  if (
    normalized.includes('3 putts') ||
    normalized.includes('three putts') ||
    normalized.includes('3putt') ||
    normalized.includes('threeputt')
  ) {
    return 'threeputt';
  }
  return null;
};

export const hazardIconForGameName = (name?: string) => {
  const normalized = (name ?? '').toLowerCase();
  if (normalized.includes('water')) return WaterIcon;
  if (normalized.includes('penalty')) return PenaltyIcon;
  if (normalized.includes('bunker')) return BunkerIcon;
  if (
    normalized.includes('3 putts') ||
    normalized.includes('three putts') ||
    normalized.includes('3putt') ||
    normalized.includes('threeputt')
  ) {
    return ThreePuttIcon;
  }
  return null;
};

export const parseHandicapValue = (raw: unknown): number | undefined => {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    const parsed = parseFloat(raw);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

export const computeNetForHole = ({
  gross,
  handicap,
  holeIndex,
  strokeIndex,
  par,
}: {
  gross?: string | number | null;
  handicap?: number | null;
  holeIndex?: number | null;
  strokeIndex?: number | null;
  par?: number | null;
}): NetResult | null => {
  if (gross === null || gross === undefined) return null;
  const grossStrokes = typeof gross === 'string' ? parseInt(gross, 10) : gross;
  if (!Number.isFinite(grossStrokes) || grossStrokes <= 0) return null;

  const normalizedHandicap = handicap !== null && handicap !== undefined ? handicap : null;
  const normalizedHoleIndex = strokeIndex ?? holeIndex ?? null;
  const normalizedPar = par !== null && par !== undefined ? par : null;

  let strokesReceived = 0;
  if (normalizedHandicap !== null && normalizedHoleIndex !== null && normalizedHoleIndex > 0) {
    const baseShots = Math.floor(normalizedHandicap / 18);
    const extraShots = normalizedHoleIndex <= normalizedHandicap % 18 ? 1 : 0;
    strokesReceived = Math.max(0, baseShots + extraShots);
  }

  const netStrokes = Math.max(1, grossStrokes - strokesReceived);
  const netVsPar = normalizedPar !== null ? netStrokes - normalizedPar : undefined;
  const netVsParLabel =
    netVsPar === undefined
      ? undefined
      : netVsPar === 0
        ? 'E'
        : netVsPar > 0
          ? `+${netVsPar}`
          : `${netVsPar}`;

  return { netStrokes, strokesReceived, netVsPar, netVsParLabel };
};

export const normalizeGameSlug = (name?: string | null) => {
  if (!name) return '';
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

export const normalizeHoleEntry = (entry: Record<string, unknown>): HoleEntry => {
  const playerId =
    (entry as { playerId?: string }).playerId ??
    (entry as { player_id?: string }).player_id ??
    (entry as { PlayerId?: string }).PlayerId ??
    (entry as { player?: string }).player ??
    null;
  const playerName =
    (entry as { playerName?: string }).playerName ??
    (entry as { player?: string }).player ??
    (entry as { Player?: string }).Player ??
    '';
  const rawPickup =
    (entry as { pickup?: unknown }).pickup ??
    (entry as { Pickup?: unknown }).Pickup;
  const pickup =
    typeof rawPickup === 'boolean'
      ? rawPickup
      : typeof rawPickup === 'string'
        ? rawPickup.toLowerCase() === 'true'
        : false;
  return {
    id: (entry as { id?: string }).id ?? '',
    roundId: (entry as { roundId?: string }).roundId ?? (entry as { RoundId?: string }).RoundId ?? '',
    playerId: playerId ?? undefined,
    holeNumber:
      (entry as { holeNumber?: number }).holeNumber ??
      (entry as { HoleNumber?: number }).HoleNumber ??
      (entry as { hole?: number }).hole ??
      0,
    strokeIndex:
      (entry as { strokeIndex?: number }).strokeIndex ??
      (entry as { StrokeIndex?: number }).StrokeIndex ??
      (entry as { holeIndex?: number }).holeIndex ??
      (entry as { HoleIndex?: number }).HoleIndex ??
      (entry as { index?: number }).index,
    playerName,
    grossStrokes:
      (entry as { grossStrokes?: number | null }).grossStrokes ??
      (entry as { GrossStrokes?: number | null }).GrossStrokes ??
      (entry as { grossStroke?: number | null }).grossStroke ??
      (entry as { GrossStroke?: number | null }).GrossStroke ??
      (entry as { strokes?: number | null }).strokes ??
      (entry as { Strokes?: number | null }).Strokes ??
      (entry as { value?: number | null }).value ??
      null,
    pickup,
    playingHandicap:
      (entry as { playingHandicap?: number }).playingHandicap ??
      (entry as { playing_handicap?: number }).playing_handicap ??
      (entry as { Handicap?: number }).Handicap ??
      (entry as { handicap?: number }).handicap ??
      null,
    createdAt:
      (entry as { updatedAt?: string }).updatedAt ??
      (entry as { UpdatedAt?: string }).UpdatedAt ??
      (entry as { createdAt?: string }).createdAt ??
      (entry as { CreatedAt?: string }).CreatedAt ??
      '',
    handicapStrokes:
      (entry as { handicapStrokes?: number }).handicapStrokes ??
      (entry as { HandicapStrokes?: number }).HandicapStrokes ??
      (entry as { handicapStrokes?: number }).handicapStrokes ??
      (entry as { StrokesReceived?: number }).StrokesReceived ??
      (entry as { strokes_received?: number }).strokes_received,
    netStrokes:
      (entry as { netStrokes?: number | null }).netStrokes ??
      (entry as { NetStrokes?: number | null }).NetStrokes ??
      null,
  };
};

export const computeStrokesReceivedForHole = (handicap: number | null | undefined, holeIndex: number | null | undefined) => {
  if (handicap === null || handicap === undefined || holeIndex === null || holeIndex === undefined || holeIndex <= 0) {
    return 0;
  }
  const base = Math.floor(handicap / 18);
  const extra = holeIndex <= (handicap % 18) ? 1 : 0;
  return Math.max(0, base + extra);
};

export const normalizeTotalsList = (totals: ScoreTotal[], players: Array<{ id: string; name: string }>): ScoreTotal[] =>
  totals.map((t) => {
    const match = players.find((p) => p.id === t.playerId || p.name === t.playerName);
    const resolvedId = t.playerId ?? match?.id ?? t.playerName ?? '';
    const resolvedName = match?.name ?? t.playerName ?? t.playerId ?? 'Okänd spelare';
    return {
      ...t,
      playerId: resolvedId,
      playerName: resolvedName,
      totalStrokes: t.totalStrokes ?? 0,
      totalRoundGamePoints: t.totalRoundGamePoints ?? (t as { totalPoints?: number }).totalPoints ?? undefined,
      tourGamesPoints: t.tourGamesPoints ?? undefined,
      pickups: t.pickups ?? 0,
    };
  });

export const normalizeHoleEvent = (entry: Record<string, unknown>): HoleEventEntry | null => {
  const playerId =
    (entry as { playerId?: string }).playerId ??
    (entry as { PlayerId?: string }).PlayerId ??
    '';
  const holeNumber =
    (entry as { holeNumber?: number }).holeNumber ??
    (entry as { HoleNumber?: number }).HoleNumber ??
    0;
  const eventType =
    (entry as { eventType?: string }).eventType ??
    (entry as { EventType?: string }).EventType ??
    '';
  if (!playerId || !eventType || !holeNumber) return null;
  const eventValue =
    (entry as { eventValue?: string }).eventValue ??
    (entry as { EventValue?: string }).EventValue ??
    null;
  const occurredAt =
    (entry as { occurredAt?: string }).occurredAt ??
    (entry as { OccurredAt?: string }).OccurredAt ??
    null;
  return { playerId, holeNumber, eventType, eventValue, occurredAt };
};
